import React, { useCallback, useEffect } from "react";
import { Route, Routes, Navigate, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import * as authentication from "../utils/auth";
import { api } from "../utils/api";
import { CurrentUserContext } from "../contexts/CurrentUserContext";
import {
  setEmail,
  setLoggedIn,
  setToolTipTitle,
  setCurrentUser,
} from "../features/user/userSlice";
import {
  setCards,
  setSelectedCard,
} from "../features/cards/cardsSlice";
import {
  setIsLoading,
  setIsAvatarPopupOpen,
  setIsProfilePopupOpen,
  setIsPlacePopupOpen,
  setIsBulkPopupOpen,
  setIsFileUploadPopupOpen,
  setIsImagePopupOpen,
  setIsConfirmationPopupOpen,
  setIsCheckoutPopupOpen,
  setIsInfoToolTipPopupOpen,
  setIsInstructionsPopupOpen,
  setEditButtonsVisible,
  setSmallPrintCounterSum,
  setBigPrintCounterSum,
  setDigitalOptionCounterSum,
  setYearbookOptionCounterSum,
  setToolTipPicture,
} from "../features/ui/uiSlice";

import Main from "./Main";
import Header from "./Header";
import Footer from "./Footer";
import Login from "./Login";
import Register from "./Register";
import ProtectedRoute from "./ProtectedRoute";

import ImagePopup from "./ImagePopup";
import ConfirmationPopup from "./ConfirmationPopup";
// import AddPlacePopup from "./AddPlacePopup";
import AddBulkPopup from "./AddBulkPopup";
import FileUploadPopup from "./FileUploadPopup";
import EditAvatarPopup from "./EditAvatarPopup";
import EditProfilePopup from "./EditProfilePopup";
import InstructionsPopup from "./InstructionsPopup";
import InfoToolTip from "./InfoToolTip";
import CheckoutPopup from "./CheckoutPopup";
import Account from "./Account/Account";
import Preloader from "./Preloader/Preloader";

import Developer from "./Developer/Developer";

export default function App() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // User state
  const email = useSelector((state) => state.user.email);
  const loggedIn = useSelector((state) => state.user.loggedIn);
  const toolTipTitle = useSelector((state) => state.user.toolTipTitle);
  const currentUser = useSelector((state) => state.user.currentUser);

  // Cards state
  const cards = useSelector((state) => state.cards.cards);
  const selectedCard = useSelector((state) => state.cards.selectedCard);

  // UI state
  const isLoading = useSelector((state) => state.ui.isLoading);
  const isAvatarPopupOpen = useSelector((state) => state.ui.isAvatarPopupOpen);
  const isProfilePopupOpen = useSelector((state) => state.ui.isProfilePopupOpen);
  const isBulkPopupOpen = useSelector((state) => state.ui.isBulkPopupOpen);
  const isFileUploadPopupOpen = useSelector((state) => state.ui.isFileUploadPopupOpen);
  const isImagePopupOpen = useSelector((state) => state.ui.isImagePopupOpen);
  const isConfirmationPopupOpen = useSelector((state) => state.ui.isConfirmationPopupOpen);
  const isCheckoutPopupOpen = useSelector((state) => state.ui.isCheckoutPopupOpen);
  const isInfoToolTipPopupOpen = useSelector((state) => state.ui.isInfoToolTipPopupOpen);
  const isInstructionsPopupOpen = useSelector((state) => state.ui.isInstructionsPopupOpen);

  const editButtonsVisible = useSelector((state) => state.ui.editButtonsVisible);
  const smallPrintCounterSum = useSelector((state) => state.ui.smallPrintCounterSum);
  const bigPrintCounterSum = useSelector((state) => state.ui.bigPrintCounterSum);
  const digitalOptionCounterSum = useSelector((state) => state.ui.digitalOptionCounterSum);
  const yearbookOptionCounterSum = useSelector((state) => state.ui.yearbookOptionCounterSum);

  // useEffect(() => {
  //   if (loggedIn) {
  //     Promise.all([api.getUserInfo(), api.getServerCards()])
  //       .then(([user, card]) => {
  //         setCurrentUser(user);
  //         setCards(card);
  //       })
  //       .catch((err) => window.alert(err))
  //   }
  // }, [loggedIn]);

  useEffect(() => {
    if (loggedIn) {
      Promise.all([api.getUserInfo(), api.getServerCards()])
        .then(([user, card]) => {
          dispatch(setEmail(user.email));
          dispatch(setLoggedIn(true));
          dispatch(setCurrentUser(user));

          // Sort cards alphabetically by name before setting the state
          dispatch(setCards(card.sort((a, b) => a.name.localeCompare(b.name))));
        })
        .catch((err) => window.alert(err));
    }
  }, [loggedIn, dispatch]);

  useEffect(() => {
    let smallTotal = 0;
    let bigTotal = 0;
    let digitalTotal = 0;
    let yearbookTotal = 0;

    cards.forEach(card => {
      if (card.owner === currentUser._id) {
        smallTotal += card.smallPrintCounter;
        bigTotal += card.bigPrintCounter;
        digitalTotal += card.downloadOption === true ? 1 : 0;
        yearbookTotal += card.yearbookOption === true ? 1 : 0;
      }
    });

    //NOTE: если пользоваться reducer, то все ниже set-ы можно запихнуть в 1 функцию. Меньше кода, легче читается
    dispatch(setEditButtonsVisible(false));
    dispatch(setSmallPrintCounterSum(smallTotal));
    dispatch(setBigPrintCounterSum(bigTotal));
    dispatch(setDigitalOptionCounterSum(digitalTotal));
    dispatch(setYearbookOptionCounterSum(yearbookTotal));
  }, [cards, currentUser, dispatch]);

  const handleBulkClick = () => dispatch(setIsBulkPopupOpen(true));
  const handleFileUploadClick = () => dispatch(setIsFileUploadPopupOpen(true));
  const handleProfileClick = () => dispatch(setIsProfilePopupOpen(true));
  const handleCheckoutClick = () => dispatch(setIsCheckoutPopupOpen(true));
  const handleAvatarClick = () => dispatch(setIsAvatarPopupOpen(true));

  function handleImageClick(card) {
    dispatch(setSelectedCard(card));
    dispatch(setIsImagePopupOpen(true));
  }

  function handleConfirmDeleteCard(card) {
    dispatch(setSelectedCard(card));
    dispatch(setIsConfirmationPopupOpen(true));
  }

  const closeAllPopups = useCallback(() => {
    dispatch(setIsAvatarPopupOpen(false));
    dispatch(setIsProfilePopupOpen(false));
    dispatch(setIsPlacePopupOpen(false));
    dispatch(setIsImagePopupOpen(false));
    dispatch(setIsInfoToolTipPopupOpen(false));
    dispatch(setIsConfirmationPopupOpen(false));
    dispatch(setIsCheckoutPopupOpen(false));
    dispatch(setIsBulkPopupOpen(false));
    dispatch(setIsFileUploadPopupOpen(false));
    dispatch(setIsInstructionsPopupOpen(false));
  }, [dispatch]);

  function handleDownloadOptionCard(card) {
    const isDownloadOption = card.downloadOption === true;
    api.changeDownloadOptionCardState(card._id, !isDownloadOption)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleYearbookOptionCard(card) {
    const isYearbookOption = card.yearbookOption === true;
    api.changeYearbookOptionCardState(card._id, !isYearbookOption)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleSmallPrintClickUp(card) {
    api
      .setSmallPrintCounterUp(card._id)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleSmallPrintClickDown(card) {
    api
      .setSmallPrintCounterDown(card._id)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleBigPrintClickUp(card) {
    api
      .setBigPrintCounterUp(card._id)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleBigPrintClickDown(card) {
    api
      .setBigPrintCounterDown(card._id)
      .then((newCard) => {
        dispatch(setCards(cards.map((c) => (c._id === card._id ? newCard : c))));
      })
      .catch((err) => window.alert(err));
  }

  function handleDeleteCard(card) {
    api
      .deleteCard(card._id)
      .then(() => {
        dispatch(setCards(cards.filter((s) => s._id !== card._id)));
      })
      .catch((err) => window.alert(err));
  }


  function handleUserUpdate(value) {
    api
      .setUserInfo(value)
      .then((res) => {
        dispatch(setCurrentUser(res));
        closeAllPopups();
      })
      .catch((err) => window.alert(err));
  }

  function handleAvatarChange(value) {
    api
      .setUserAvatar(value)
      .then((res) => {
        dispatch(setCurrentUser(res));
        closeAllPopups();
      })
      .catch((err) => window.alert(err));
  }

  function handlePlaceSubmit(card) {
    api
      .handleAddNewCard(card)
      .then((newCard) => {
        dispatch(setCards([newCard, ...cards]));
        closeAllPopups();
      })
      .catch((err) => window.alert(err));
  }

  function onSuccess(title = "Success!") {
    dispatch(setToolTipTitle(title));
    dispatch(setToolTipPicture("success"));
    dispatch(setIsInfoToolTipPopupOpen(true));
  }

  function onFail(title = "Something went wrong. Please try again.") {
    dispatch(setToolTipTitle(title));
    dispatch(setToolTipPicture("error"));
    dispatch(setIsInfoToolTipPopupOpen(true));
  }

  useEffect(() => {
    if (localStorage.getItem("jwt")) {
      const jwt = localStorage.getItem("jwt");
      authentication
        .getData(jwt)
        .then((res) => {
          dispatch(setLoggedIn(true));
          dispatch(setEmail(res.email));

          // Check if the current path is /developer or /account
          if (
            window.location.pathname !== "/developer" &&
            window.location.pathname !== "/account"
          ) {
            navigate("/", { replace: true });
          }
        })
        .catch((err) => console.log(err));
    }
  }, [navigate, dispatch]);

  function handleLogin(email, password) {
    dispatch(setIsLoading(true));
    authentication
      .authorize(email, password)
      .then((res) => {
        localStorage.setItem("jwt", res.token);
        dispatch(setLoggedIn(true));
        navigate("/", { replace: true });
      })
      .catch((err) => {
        onFail(err);
        console.log(err);
      })
      .finally(() => dispatch(setIsLoading(false)));
  }

  function handleRegistration(email, password) {
    authentication
      .register(email, password)
      .then(() => {
        navigate("/sign-in", { replace: true });
        onSuccess("Welcome!");
        handleLogin(email, password);
      })
      .catch((err) => {
        onFail(err);
        console.log(err);
      });
  }

  function handleDeleteUser() {
    api
      .deleteUser()
      .then(() => {
        signOut();
        navigate("/sign-in", { replace: true });
        onSuccess("You're off the Internet!");
      })
      .catch((err) => {
        onFail(err);
        console.log(err);
      });
  }

  function signOut() {
    localStorage.removeItem("jwt");
    dispatch(setLoggedIn(false));
    navigate("/sign-in", { replace: true });
  }

  const handleKeyboard = useCallback(({ repeat, ctrlKey, key }) => {
    if (key === "Escape") closeAllPopups();
    if (repeat) return;
    if (ctrlKey && key === "\\") dispatch(setEditButtonsVisible(!editButtonsVisible));
  }, [closeAllPopups, dispatch, editButtonsVisible]);

  useEffect(() => {
    document.addEventListener("keydown", handleKeyboard);
    return () => document.removeEventListener("keydown", handleKeyboard);
  }, [handleKeyboard]);

  useEffect(() => {
    function handleClick(e) {
      if (e.target.classList.contains("popup_opened")) {
        closeAllPopups();
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => {
      document.removeEventListener("mousedown", handleClick);
    };
  }, [closeAllPopups]);

  const toolTipPicture = useSelector((state) => state.ui.toolTipPicture);

  // Add this function
  function handleInstructionsClick() {
    dispatch(setIsInstructionsPopupOpen(true));
  }

  return (
    <CurrentUserContext.Provider value={currentUser}>
      <div className={loggedIn ? "body page" : "body_colorful"}>
        <Header
          email={email}
          signOut={signOut}
          loggedIn={loggedIn}
          smallPrintCounterSum={smallPrintCounterSum}
          bigPrintCounterSum={bigPrintCounterSum}
          digitalOptionCounterSum={digitalOptionCounterSum}
          onCheckoutClick={handleCheckoutClick}
        />

        {isLoading && <Preloader />}

        <Routes>
          <Route
            path="/"
            element={
              <ProtectedRoute
                loggedIn={loggedIn}
                onAvatarEdit={handleAvatarClick}
                onProfileEdit={handleProfileClick}
                onInstructionsClick={handleInstructionsClick}
                //onAdd={handleAddClick}
                onAdd={handlePlaceSubmit}
                onBulk={handleBulkClick}
                onFileUpload={handleFileUploadClick}
                onDownloadOption={handleDownloadOptionCard}
                onYearbookOption={handleYearbookOptionCard}
                onHandleConfirmDeleteCard={handleConfirmDeleteCard}
                onHandleAvatarChange={handleAvatarChange}
                onSmallPrintClickUp={handleSmallPrintClickUp}
                onSmallPrintClickDown={handleSmallPrintClickDown}
                onBigPrintClickUp={handleBigPrintClickUp}
                onBigPrintClickDown={handleBigPrintClickDown}
                onImageClick={handleImageClick}
                cards={cards}
                editButtonsVisible={editButtonsVisible}
                element={Main}
              />
            }
          />
          <Route
            path="/account"
            element={
              <ProtectedRoute
                path="/account"
                loggedIn={loggedIn}
                handleDeleteUser={handleDeleteUser}
                element={Account}
                email={email}
              />
            }
          />
          <Route
            path="/developer"
            element={
              <ProtectedRoute
                path="/developer"
                loggedIn={loggedIn}
                element={Developer}
              />
            }
          />
          <Route
            path="/sign-in"
            element={<Login onLogin={handleLogin} />}
          />
          <Route
            path="/sign-up"
            element={<Register handleRegistration={handleRegistration} />}
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>

        {loggedIn && <Footer />}

        <EditAvatarPopup
          isOpen={isAvatarPopupOpen}
          onClose={closeAllPopups}
          onAvatarChange={handleAvatarChange}
        />
        <EditProfilePopup
          isOpen={isProfilePopupOpen}
          onClose={closeAllPopups}
          onUserUpdate={handleUserUpdate}
        />
        <InstructionsPopup
          isOpen={isInstructionsPopupOpen}
          onClose={closeAllPopups}
        />
        {/* <AddPlacePopup
          isOpen={isPlacePopupOpen}
          onClose={closeAllPopups}
          onAdd={handlePlaceSubmit}
        /> */}
        <AddBulkPopup
          isOpen={isBulkPopupOpen}
          onClose={closeAllPopups}
          onAdd={handlePlaceSubmit}
        />
        <FileUploadPopup
          isOpen={isFileUploadPopupOpen}
          onClose={closeAllPopups}
        />
        <ImagePopup
          isOpen={isImagePopupOpen}
          onClose={closeAllPopups}
          card={selectedCard}
          cards={cards}
        />
        <ConfirmationPopup
          isOpen={isConfirmationPopupOpen}
          onClose={closeAllPopups}
          card={selectedCard}
          onDeleteCard={handleDeleteCard}
        />
        <InfoToolTip
          isOpen={isInfoToolTipPopupOpen}
          onClose={closeAllPopups}
          title={toolTipTitle}
          toolTipPicture={toolTipPicture}
        />
        <CheckoutPopup
          isOpen={isCheckoutPopupOpen}
          onClose={closeAllPopups}
          isLoading={isLoading}
          smallPrintCounterSum={smallPrintCounterSum}
          bigPrintCounterSum={bigPrintCounterSum}
          digitalOptionCounterSum={digitalOptionCounterSum}
          yearbookOptionCounterSum={yearbookOptionCounterSum}
          email={email}
          cards={cards}
          onSuccess={onSuccess}
          onFail={onFail}
        />
      </div>
    </CurrentUserContext.Provider>
  );
}
